import React, { useState, useEffect } from 'react';
import { FaExclamationTriangle, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';

/*
 * In-app replacements for window.confirm / window.prompt / window.alert.
 *
 * Native dialogs are not reliable here: once a user ticks the browser's
 * "don't allow this page to create more dialogs" box, every later call returns
 * false / null instantly with nothing shown, so the action silently never
 * happens and the page looks broken until it is reloaded.
 *
 * These components reuse the .modal-overlay / .modal-content / .action-button
 * styles that each page already declares in its own <style> block.
 */

const TONES = {
    warning: { Icon: FaExclamationTriangle, color: '#fcd34d' },
    danger: { Icon: FaExclamationTriangle, color: '#fca5a5' },
    error: { Icon: FaTimesCircle, color: '#fca5a5' },
    success: { Icon: FaCheckCircle, color: '#48bb78' }
};

const ModalShell = ({ onClose, tone, title, maxWidth = '420px', children }) => {
    const { Icon, color } = TONES[tone] || TONES.warning;
    return (
        <div className="modal-overlay" style={{ zIndex: 9999 }} onClick={onClose}>
            <div
                className="modal-content"
                style={{ maxWidth, textAlign: 'center', padding: '2rem' }}
                onClick={e => e.stopPropagation()}
            >
                <div style={{ textAlign: 'center' }}>
                    <Icon style={{ fontSize: '3rem', color, marginBottom: '1rem' }} />
                </div>
                <h3 style={{ margin: '0 0 1rem 0', color: 'var(--text-color)' }}>{title}</h3>
                {children}
            </div>
        </div>
    );
};

const bodyStyle = {
    color: 'var(--light-text)',
    marginBottom: '1.5rem',
    lineHeight: '1.6',
    fontSize: '0.95rem'
};

// Replaces window.confirm
export const ConfirmModal = ({
    isOpen,
    title,
    message,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    tone = 'warning',
    confirmVariant = 'danger',
    isBusy = false,
    onConfirm,
    onClose
}) => {
    if (!isOpen) return null;

    return (
        <ModalShell onClose={isBusy ? () => { } : onClose} tone={tone} title={title}>
            <div style={bodyStyle}>{message}</div>
            <div style={{ display: 'flex', gap: '1rem' }}>
                <button
                    type="button"
                    className="action-button secondary"
                    style={{ flex: 1, justifyContent: 'center' }}
                    onClick={onClose}
                    disabled={isBusy}
                >
                    {cancelLabel}
                </button>
                <button
                    type="button"
                    className={`action-button ${confirmVariant}`}
                    style={{ flex: 1, justifyContent: 'center' }}
                    onClick={onConfirm}
                    disabled={isBusy}
                >
                    {confirmLabel}
                </button>
            </div>
        </ModalShell>
    );
};

// Replaces window.alert
export const NoticeModal = ({
    isOpen,
    title,
    message,
    buttonLabel = 'OK',
    tone = 'success',
    onClose
}) => {
    if (!isOpen) return null;

    return (
        <ModalShell onClose={onClose} tone={tone} title={title}>
            <div style={bodyStyle}>{message}</div>
            <button
                type="button"
                className="action-button primary"
                style={{ width: '100%', justifyContent: 'center' }}
                onClick={onClose}
            >
                {buttonLabel}
            </button>
        </ModalShell>
    );
};

// Replaces window.prompt
export const PromptModal = ({
    isOpen,
    title,
    message,
    label,
    placeholder,
    initialValue = '',
    confirmLabel = 'Save',
    cancelLabel = 'Cancel',
    requiredMessage = 'This field is required.',
    tone = 'warning',
    onConfirm,
    onClose
}) => {
    const [value, setValue] = useState(initialValue);
    const [error, setError] = useState('');

    useEffect(() => {
        if (isOpen) {
            setValue(initialValue);
            setError('');
        }
    }, [isOpen, initialValue]);

    if (!isOpen) return null;

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!value.trim()) {
            setError(requiredMessage);
            return;
        }
        onConfirm(value.trim());
    };

    return (
        <ModalShell onClose={onClose} tone={tone} title={title} maxWidth="460px">
            <form onSubmit={handleSubmit}>
                {message && <div style={{ ...bodyStyle, marginBottom: '1rem', textAlign: 'left' }}>{message}</div>}
                <div className="input-group" style={{ textAlign: 'left', marginBottom: '1rem' }}>
                    {label && <label className="input-label" htmlFor="prompt-modal-input">{label}</label>}
                    <textarea
                        id="prompt-modal-input"
                        value={value}
                        onChange={(e) => { setValue(e.target.value); if (error) setError(''); }}
                        placeholder={placeholder}
                        rows="3"
                        autoFocus
                    />
                </div>
                {error && <p className="form-feedback error small" style={{ marginBottom: '1rem' }}>{error}</p>}
                <div style={{ display: 'flex', gap: '1rem' }}>
                    <button
                        type="button"
                        className="action-button secondary"
                        style={{ flex: 1, justifyContent: 'center' }}
                        onClick={onClose}
                    >
                        {cancelLabel}
                    </button>
                    <button
                        type="submit"
                        className="action-button primary"
                        style={{ flex: 1, justifyContent: 'center' }}
                    >
                        {confirmLabel}
                    </button>
                </div>
            </form>
        </ModalShell>
    );
};
